import { DomainError } from './errors.js';
import { type FormField, type FormSchema, isFieldInAudience } from './form-schema.js';

export const FORM_SCHEMA_CHANGE_KINDS = [
  'field_added_required',
  'field_added_optional',
  'field_removed',
  'type_changed',
  'rule_tightened',
  'rule_loosened',
  'option_removed',
  'option_added',
  'option_label_changed',
  'options_reordered',
  'visibility_changed',
  'portal_field_added_required',
  'portal_field_added_optional',
  'portal_field_removed',
  'label_changed',
  'help_changed',
  'placeholder_changed',
  'width_changed',
  'default_changed',
  'audience_changed',
  'fields_reordered',
  'direction_changed',
  'variant_changed',
] as const;

export type FormSchemaChangeKind = (typeof FORM_SCHEMA_CHANGE_KINDS)[number];

export type FormChangeValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | { field: string; equals: string | number | boolean | null };

export type FormSchemaChange = {
  key: string | null;
  kind: FormSchemaChangeKind;
  breaking: boolean;
  rule: string | null;
  option: string | null;
  from: FormChangeValue;
  to: FormChangeValue;
};

export type FormSchemaDiff = {
  breaking: boolean;
  changes: FormSchemaChange[];
};

type RuleDirection = 'lower_bound' | 'upper_bound' | 'flag' | 'exact' | 'allow_list';

const RULE_DIRECTIONS: Record<string, RuleDirection> = {
  minLength: 'lower_bound',
  min: 'lower_bound',
  minDate: 'lower_bound',
  minSelected: 'lower_bound',
  maxLength: 'upper_bound',
  max: 'upper_bound',
  maxDate: 'upper_bound',
  maxSelected: 'upper_bound',
  maxSizeBytes: 'upper_bound',
  maxCount: 'upper_bound',
  integer: 'flag',
  mustBeTrue: 'flag',
  pattern: 'exact',
  step: 'exact',
  accept: 'allow_list',
};

type Bound = string | number;
type Tightness = 'tightened' | 'loosened' | 'unchanged';

function change(
  kind: FormSchemaChangeKind,
  breaking: boolean,
  values: Partial<Omit<FormSchemaChange, 'kind' | 'breaking'>> = {},
): FormSchemaChange {
  return {
    key: values.key ?? null,
    kind,
    breaking,
    rule: values.rule ?? null,
    option: values.option ?? null,
    from: values.from ?? null,
    to: values.to ?? null,
  };
}

function isBound(value: unknown): value is Bound {
  return typeof value === 'string' || typeof value === 'number';
}

function compareBound(direction: 'lower_bound' | 'upper_bound', from: unknown, to: unknown) {
  const fromSet = isBound(from);
  const toSet = isBound(to);
  if (!fromSet && !toSet) return 'unchanged';
  if (!fromSet) return 'tightened';
  if (!toSet) return 'loosened';
  if (from === to) return 'unchanged';
  const raised = to > from;
  if (direction === 'lower_bound') return raised ? 'tightened' : 'loosened';
  return raised ? 'loosened' : 'tightened';
}

function compareFlag(from: unknown, to: unknown): Tightness {
  const before = from === true;
  const after = to === true;
  if (before === after) return 'unchanged';
  return after ? 'tightened' : 'loosened';
}

function compareExact(from: unknown, to: unknown): Tightness {
  const before = from ?? null;
  const after = to ?? null;
  if (before === after) return 'unchanged';
  return after === null ? 'loosened' : 'tightened';
}

function compareAllowList(from: unknown, to: unknown): Tightness {
  const before = Array.isArray(from) ? (from as string[]) : null;
  const after = Array.isArray(to) ? (to as string[]) : null;
  if (before === null && after === null) return 'unchanged';
  if (before === null) return 'tightened';
  if (after === null) return 'loosened';
  const dropped = before.some((entry) => !after.includes(entry));
  const added = after.some((entry) => !before.includes(entry));
  if (dropped) return 'tightened';
  return added ? 'loosened' : 'unchanged';
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

function compareRule(rule: string, from: unknown, to: unknown): Tightness {
  const direction = RULE_DIRECTIONS[rule];
  switch (direction) {
    case 'lower_bound':
    case 'upper_bound':
      return compareBound(direction, from, to);
    case 'flag':
      return compareFlag(from, to);
    case 'exact':
      return compareExact(from, to);
    case 'allow_list':
      return compareAllowList(from, to);
    default:
      return sameJson(from, to) ? 'unchanged' : 'tightened';
  }
}

function asChangeValue(value: unknown): FormChangeValue {
  if (value === undefined) return null;
  return value as FormChangeValue;
}

function rulesOf(field: FormField): Record<string, unknown> {
  return (field.rules ?? {}) as Record<string, unknown>;
}

function ruleChanges(before: FormField, after: FormField): FormSchemaChange[] {
  const key = after.key;
  const previous = rulesOf(before);
  const next = rulesOf(after);
  const names = [...new Set([...Object.keys(previous), ...Object.keys(next)])].sort();
  const changes: FormSchemaChange[] = [];

  for (const rule of names) {
    let tightness = compareRule(rule, previous[rule], next[rule]);
    const stepOriginMoved =
      rule === 'min' && next.step != null && (previous.min ?? null) !== (next.min ?? null);
    if (stepOriginMoved) tightness = 'tightened';
    if (tightness === 'unchanged') continue;

    const tightened = tightness === 'tightened';
    changes.push(
      change(tightened ? 'rule_tightened' : 'rule_loosened', tightened, {
        key,
        rule,
        from: asChangeValue(previous[rule]),
        to: asChangeValue(next[rule]),
      }),
    );
  }

  const requiredness = compareFlag(before.required, after.required);
  if (requiredness !== 'unchanged') {
    const tightened = requiredness === 'tightened';
    changes.push(
      change(tightened ? 'rule_tightened' : 'rule_loosened', tightened, {
        key,
        rule: 'required',
        from: before.required,
        to: after.required,
      }),
    );
  }

  return changes;
}

type Option = { value: string; label: string };

function optionsOf(field: FormField): Option[] {
  return 'options' in field && Array.isArray(field.options) ? field.options : [];
}

function optionChanges(before: FormField, after: FormField): FormSchemaChange[] {
  const key = after.key;
  const previous = optionsOf(before);
  const next = optionsOf(after);
  const nextByValue = new Map(next.map((option) => [option.value, option]));
  const previousValues = new Set(previous.map((option) => option.value));
  const changes: FormSchemaChange[] = [];

  for (const option of previous) {
    const counterpart = nextByValue.get(option.value);
    if (!counterpart) {
      changes.push(
        change('option_removed', true, { key, option: option.value, from: option.label }),
      );
    } else if (counterpart.label !== option.label) {
      changes.push(
        change('option_label_changed', false, {
          key,
          option: option.value,
          from: option.label,
          to: counterpart.label,
        }),
      );
    }
  }

  for (const option of next) {
    if (previousValues.has(option.value)) continue;
    changes.push(change('option_added', false, { key, option: option.value, to: option.label }));
  }

  const keptBefore = previous.map((o) => o.value).filter((value) => nextByValue.has(value));
  const keptAfter = next.map((o) => o.value).filter((value) => previousValues.has(value));
  if (!sameJson(keptBefore, keptAfter)) {
    changes.push(change('options_reordered', false, { key, from: keptBefore, to: keptAfter }));
  }

  return changes;
}

/**
 * Requiredness is enforced only while a field is visible, so moving or removing
 * the condition of a required field can demand a value an old document never
 * had. Adding a condition, or any change on an optional field, cannot.
 */
function visibilityChange(before: FormField, after: FormField): FormSchemaChange | null {
  const from = before.visibleOn ?? null;
  const to = after.visibleOn ?? null;
  if (sameJson(from, to)) return null;
  const breaking = after.required && from !== null;
  return change('visibility_changed', breaking, {
    key: after.key,
    from: asChangeValue(from),
    to: asChangeValue(to),
  });
}

function presentationChanges(before: FormField, after: FormField): FormSchemaChange[] {
  const key = after.key;
  const changes: FormSchemaChange[] = [];
  const compare = (kind: FormSchemaChangeKind, rule: string | null, from: unknown, to: unknown) => {
    if (sameJson(from, to)) return;
    changes.push(
      change(kind, false, { key, rule, from: asChangeValue(from), to: asChangeValue(to) }),
    );
  };

  compare('label_changed', null, before.label, after.label);
  compare('help_changed', null, before.help ?? null, after.help ?? null);
  compare('placeholder_changed', null, before.placeholder ?? null, after.placeholder ?? null);
  compare('width_changed', null, before.widthMd, after.widthMd);
  compare('default_changed', null, before.default ?? null, after.default ?? null);
  if (before.type === 'radio' && after.type === 'radio') {
    compare('direction_changed', null, before.direction, after.direction);
    compare('variant_changed', null, before.variant, after.variant);
  }
  return changes;
}

/**
 * The audience flags are judged by what they do to a submission, not by
 * whether they moved. `admin` holds every field, so only portal membership can
 * change, and `isFieldInAudience` is the same test the compiler and the
 * renderer apply.
 *
 * A required field entering the portal audience newly demands a value of every
 * portal submission, which is exactly what breaking means. An optional field
 * entering demands nothing. A field leaving the portal stops being captured
 * there but is still held — and still validated — under `admin`, so no
 * submission that was valid becomes invalid. A flag that moves without moving
 * membership changes no audience's shape and is reported as it always was.
 */
function audienceChanges(before: FormField, after: FormField): FormSchemaChange[] {
  const key = after.key;
  const was = isFieldInAudience(before, 'portal');
  const now = isFieldInAudience(after, 'portal');

  if (was && !now) return [change('portal_field_removed', false, { key, from: before.required })];
  if (!was && now) {
    const kind = after.required ? 'portal_field_added_required' : 'portal_field_added_optional';
    return [change(kind, after.required, { key, to: after.required })];
  }

  const changes: FormSchemaChange[] = [];
  const compare = (rule: string, from: boolean, to: boolean) => {
    if (from === to) return;
    changes.push(change('audience_changed', false, { key, rule, from, to }));
  };
  compare('portalVisible', before.portalVisible, after.portalVisible);
  compare('adminOnly', before.adminOnly, after.adminOnly);
  return changes;
}

function fieldChanges(before: FormField, after: FormField): FormSchemaChange[] {
  if (before.type !== after.type) {
    return [change('type_changed', true, { key: after.key, from: before.type, to: after.type })];
  }
  const visibility = visibilityChange(before, after);
  return [
    ...ruleChanges(before, after),
    ...optionChanges(before, after),
    ...(visibility ? [visibility] : []),
    ...audienceChanges(before, after),
    ...presentationChanges(before, after),
  ];
}

/**
 * The compatibility diff of decision 0012, from the published document to the
 * one being published. **Breaking** means a document valid under `previous`
 * may be invalid under `next`, or holds a value `next` has no place for: a
 * field added as required, a field removed, a type changed, an option value
 * removed, a rule tightened. Everything else is compatible.
 *
 * Fields are matched by `key`. The document carries no other identity, so a
 * renamed key cannot be told apart from a removal and an addition and is
 * reported as exactly that — both halves, the removal breaking.
 *
 * The diff never decides whether a publish happens: every publish bumps. It
 * warns the administrator and fills `change_summary`.
 */
export function diffFormSchemas(previous: FormSchema, next: FormSchema): FormSchemaDiff {
  const previousByKey = new Map(previous.fields.map((field) => [field.key, field]));
  const nextKeys = new Set(next.fields.map((field) => field.key));
  const changes: FormSchemaChange[] = [];

  for (const field of previous.fields) {
    if (nextKeys.has(field.key)) continue;
    changes.push(change('field_removed', true, { key: field.key, from: field.type }));
  }

  for (const field of next.fields) {
    const before = previousByKey.get(field.key);
    if (!before) {
      changes.push(
        change(field.required ? 'field_added_required' : 'field_added_optional', field.required, {
          key: field.key,
          to: field.type,
        }),
      );
      continue;
    }
    changes.push(...fieldChanges(before, field));
  }

  const keptBefore = previous.fields.map((f) => f.key).filter((key) => nextKeys.has(key));
  const keptAfter = next.fields.map((f) => f.key).filter((key) => previousByKey.has(key));
  if (!sameJson(keptBefore, keptAfter)) {
    changes.push(change('fields_reordered', false, { from: keptBefore, to: keptAfter }));
  }

  return { breaking: changes.some((item) => item.breaking), changes };
}

function describeChange(item: FormSchemaChange): string {
  const subject = [item.key, item.rule ?? item.option].filter(Boolean).join('.');
  return subject ? `${item.kind}(${subject})` : item.kind;
}

/**
 * A stable English line for `campaign_form_versions.change_summary`. It is a
 * record of codes, not prose for a donor, so it is not translated.
 */
export function summarizeFormSchemaDiff(diff: FormSchemaDiff): string {
  if (diff.changes.length === 0) return 'no changes';
  const breaking = diff.changes.filter((item) => item.breaking).map(describeChange);
  const compatible = diff.changes.filter((item) => !item.breaking).map(describeChange);
  return [
    breaking.length > 0 ? `breaking: ${breaking.join(', ')}` : null,
    compatible.length > 0 ? `compatible: ${compatible.join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' | ');
}

export type FormSchemaSummaryEntry = {
  kind: FormSchemaChangeKind;
  breaking: boolean;
  key: string | null;
  detail: string | null;
};

const SUMMARY_SECTION = /^(breaking|compatible): (.*)$/;
const KIND_ALTERNATION = FORM_SCHEMA_CHANGE_KINDS.join('|');
const SUMMARY_ENTRY = new RegExp(
  `(${KIND_ALTERNATION})(?:\\((.*?)\\))?(?=, (?:${KIND_ALTERNATION})\\b|$)`,
  'g',
);

function isChangeKind(value: string): value is FormSchemaChangeKind {
  return (FORM_SCHEMA_CHANGE_KINDS as readonly string[]).includes(value);
}

/**
 * Reads back a line `summarizeFormSchemaDiff` wrote, so a history screen can
 * translate it. A summary written any other way — a seed's prose, say — is
 * `null`, and the caller shows it as it is. `detail` is the rule or option
 * after the key.
 */
export function parseFormSchemaSummary(summary: string): FormSchemaSummaryEntry[] | null {
  if (summary === 'no changes') return [];
  const entries: FormSchemaSummaryEntry[] = [];
  for (const section of summary.split(' | ')) {
    const match = SUMMARY_SECTION.exec(section);
    if (!match) return null;
    const breaking = match[1] === 'breaking';
    const body = match[2] ?? '';
    let consumed = 0;
    for (const entry of body.matchAll(SUMMARY_ENTRY)) {
      if (entry.index !== consumed) return null;
      consumed = entry.index + entry[0].length + 2;
      const kind = entry[1] ?? '';
      if (!isChangeKind(kind)) return null;
      const subject = entry[2] ?? null;
      const dot = subject === null ? -1 : subject.indexOf('.');
      const key = subject === null ? null : dot === -1 ? subject : subject.slice(0, dot);
      const detail = subject === null || dot === -1 ? null : subject.slice(dot + 1);
      entries.push({ kind, breaking, key: kind === 'fields_reordered' ? null : key, detail });
    }
    if (consumed !== body.length + 2) return null;
  }
  return entries;
}

export type FormPublishPlan = {
  previousVersion: number;
  version: number;
  inPlace: boolean;
  document: FormSchema;
  diff: FormSchemaDiff;
  changeSummary: string;
};

export type FormPublishInput = {
  currentVersion: number;
  currentDocument: FormSchema;
  everyPeriodDraft: boolean;
  basedOnVersion: number;
  next: FormSchema;
};

/**
 * Decides what a publish does, so the dry run and the real publish cannot
 * disagree.
 *
 * - A document edited from any version but the current one is refused as
 *   stale, so the diff the administrator saw is the diff that is applied.
 * - Every publish creates version `current + 1`, including one that changes
 *   nothing, because deciding what counts as cosmetic is the thing 0012
 *   refuses to do.
 * - Except in place: while every period of the campaign is still `draft`, no
 *   donation can have been captured under the current version, so it is
 *   rewritten without a bump. Version 0 is never rewritten — it has no
 *   `campaign_form_versions` row for a donation to point at — so the first
 *   publish always creates version 1.
 */
export function planFormPublish(input: FormPublishInput): FormPublishPlan {
  if (input.basedOnVersion !== input.currentVersion) {
    throw new DomainError('CONFLICT', 'The form schema was published again since it was loaded', {
      reason: 'CAMPAIGN_FORM_VERSION_STALE',
      currentVersion: input.currentVersion,
      basedOnVersion: input.basedOnVersion,
    });
  }

  const inPlace = input.currentVersion > 0 && input.everyPeriodDraft;
  const version = inPlace ? input.currentVersion : input.currentVersion + 1;
  const diff = diffFormSchemas(input.currentDocument, input.next);

  return {
    previousVersion: input.currentVersion,
    version,
    inPlace,
    document: { version, fields: input.next.fields },
    diff,
    changeSummary: summarizeFormSchemaDiff(diff),
  };
}
